import { Alert } from 'src/design/atoms/Alert';
import { Anchor } from 'src/design/atoms/Anchor';
import { Group } from 'src/design/atoms/Group';
import { PinInput } from 'src/design/atoms/PinInput';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { TextInput } from 'src/design/atoms/TextInput';
import React from 'react';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { getErrorMessage } from 'src/utils';

const RESEND_COOLDOWN_SECONDS = 30;
const CODE_LENGTH = 6;

const digitsOnly = (value: string): string =>
  value.replace(/\D/g, '').slice(0, 10);

const formatPhoneDisplay = (digits: string): string => {
  const area = digits.slice(0, 3);
  const prefix = digits.slice(3, 6);
  const line = digits.slice(6, 10);
  if (digits.length > 6) return `(${area}) ${prefix}-${line}`;
  if (digits.length > 3) return `(${area}) ${prefix}`;
  if (digits.length > 0) return `(${area}`;
  return '';
};

// Mirrors the backend's ^\+1[2-9]\d{9}$ (PhoneNumberValidator.IsValidUsE164)
// so the FE rejects an obviously invalid number before spending an OTP send.
const isValidUsPhoneDigits = (digits: string): boolean =>
  /^[2-9]\d{9}$/.test(digits);

export interface IPhoneVerificationStepperProps {
  /** E.164 phone number, if the user already has one on file (re-verify flow). */
  initialPhone?: string;
  /** Copy shown above the phone-entry step. */
  description?: React.ReactNode;
  /** Sends the OTP; reject with the caught error to surface it inline (e.g. 429 rate limit). */
  onRequestCode: (phoneNumberE164: string) => Promise<void>;
  /** Verifies the entered code; reject with the caught error to surface it inline (wrong/expired code). */
  onVerifyCode: (code: string) => Promise<void>;
  onVerified: () => void;
  /** Optional "back"/"cancel" affordance, e.g. to close a hosting modal. */
  onCancel?: () => void;
}

/** @registry Two-step (enter phone -> enter OTP) phone verification flow shared by the SMS consent interstitial and the account profile phone section; callers own the actual API calls via onRequestCode/onVerifyCode props. */
export const PhoneVerificationStepper: React.FC<
  IPhoneVerificationStepperProps
> = ({
  initialPhone,
  description,
  onRequestCode,
  onVerifyCode,
  onVerified,
  onCancel,
}) => {
  const [step, setStep] = React.useState<'phone' | 'code'>('phone');
  const [phoneDigits, setPhoneDigits] = React.useState(() =>
    digitsOnly(initialPhone ?? ''),
  );
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [sending, setSending] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);
  const [secondsLeft, setSecondsLeft] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const isValidPhone = isValidUsPhoneDigits(phoneDigits);

  const requestCode = async () => {
    setError(null);
    setSending(true);
    try {
      await onRequestCode(`+1${phoneDigits}`);
      setCode('');
      setStep('code');
      setSecondsLeft(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const handleSendCode = () => {
    if (!isValidPhone) {
      setError('Enter a valid US phone number.');
      return;
    }
    requestCode();
  };

  const handleVerify = async (value: string) => {
    if (value.length !== CODE_LENGTH || verifying) return;
    setError(null);
    setVerifying(true);
    try {
      await onVerifyCode(value);
      onVerified();
    } catch (err) {
      setError(getErrorMessage(err));
      setCode('');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Stack maw={360}>
      {description && (
        <Text size="sm" c="dimmed">
          {description}
        </Text>
      )}
      {error && (
        <Alert color="red" title="Something went wrong">
          {error}
        </Alert>
      )}
      {step === 'phone' ? (
        <>
          <TextInput
            label="Phone Number"
            placeholder="(555) 555-5555"
            value={formatPhoneDisplay(phoneDigits)}
            onChange={(e) => setPhoneDigits(digitsOnly(e.target.value))}
            error={
              phoneDigits.length === 10 && !isValidPhone
                ? 'Enter a valid US phone number.'
                : undefined
            }
          />
          <Group justify="flex-end">
            {onCancel && (
              <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
            )}
            <BasicButton
              onClick={handleSendCode}
              loading={sending}
              disabled={!isValidPhone}
            >
              Send Code
            </BasicButton>
          </Group>
        </>
      ) : (
        <>
          <Text size="sm">
            Enter the 6-digit code we texted to{' '}
            {formatPhoneDisplay(phoneDigits)}.
          </Text>
          <PinInput
            length={CODE_LENGTH}
            type="number"
            value={code}
            onChange={setCode}
            onComplete={handleVerify}
            disabled={verifying}
            aria-label="Verification code"
          />
          <Group justify="space-between">
            <Anchor
              component="button"
              type="button"
              size="sm"
              disabled={secondsLeft > 0 || sending}
              onClick={requestCode}
            >
              {secondsLeft > 0
                ? `Resend code (${secondsLeft}s)`
                : 'Resend code'}
            </Anchor>
            <BasicButton
              onClick={() => handleVerify(code)}
              loading={verifying}
              disabled={code.length !== CODE_LENGTH}
            >
              Verify
            </BasicButton>
          </Group>
          {onCancel && (
            <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
          )}
        </>
      )}
    </Stack>
  );
};
